import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTicket,
  getOpenTicketCount,
  getTicketDetail,
  listTickets,
  markTicketRead,
  sendMessage,
} from './api';
import type {
  CreateTicketInput,
  SendMessageInput,
  SupportTicket,
  SupportTicketDetail,
  TicketTab,
} from './types';

export const supportKeys = {
  all: ['support'] as const,
  openCount: () => [...supportKeys.all, 'open-count'] as const,
  list: (tab: TicketTab) => [...supportKeys.all, 'list', tab] as const,
  detail: (id: string) => [...supportKeys.all, 'detail', id] as const,
};

/** Open/Active ticket count. Drives the on-open limit gate + the Active tab badge. */
export function useOpenTicketCount(enabled = true) {
  return useQuery({
    queryKey: supportKeys.openCount(),
    queryFn: getOpenTicketCount,
    enabled,
    staleTime: 0,
  });
}

/** Raise a ticket. On success the open-count is invalidated so the gate stays accurate. */
export function useCreateSupportTicket() {
  const qc = useQueryClient();
  return useMutation<SupportTicket, unknown, CreateTicketInput>({
    mutationFn: (input) => createTicket(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supportKeys.openCount() });
      void qc.invalidateQueries({ queryKey: supportKeys.list('active') });
    },
  });
}

/** The grower's tickets for one tab (story 101970). Refetched whenever the drawer opens. */
export function useSupportTickets(tab: TicketTab, enabled = true) {
  return useQuery({
    queryKey: supportKeys.list(tab),
    queryFn: () => listTickets(tab),
    enabled,
    staleTime: 0,
    // Refetch every time the listing drawer (re)opens — the spec's manual-refresh model
    // (story 101970 §9). Explicit so the guarantee does not silently depend on the drawer
    // unmounting via `destroyOnHidden`.
    refetchOnMount: 'always',
  });
}

/** Full conversation for the detail drawer (enabled only while it is open). */
export function useSupportTicketDetail(id: string, enabled = true) {
  return useQuery({
    queryKey: supportKeys.detail(id),
    queryFn: () => getTicketDetail(id),
    enabled: enabled && !!id,
  });
}

/** Post a reply; updates the detail cache and invalidates both tab lists (preview/sort). */
export function useSendMessage(id: string) {
  const qc = useQueryClient();
  return useMutation<SupportTicketDetail, unknown, SendMessageInput>({
    mutationFn: (input) => sendMessage(id, input),
    onSuccess: (detail) => {
      qc.setQueryData(supportKeys.detail(id), detail);
      void qc.invalidateQueries({ queryKey: supportKeys.list('active') });
      void qc.invalidateQueries({ queryKey: supportKeys.list('closed') });
    },
  });
}

/** Mark a ticket read; refreshes both tab lists so the unread indicator clears. */
export function useMarkTicketRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markTicketRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supportKeys.list('active') });
      void qc.invalidateQueries({ queryKey: supportKeys.list('closed') });
    },
  });
}
